<template>
  <q-table
    :columns="cols"
    :rows="rows"
    class="external-dns-table"
    :class="{ empty: rows.length === 0 }"
    ref="adfRecordsTable"
    row-key="id"
    flat
    v-model:pagination="pagination"
    :loading="loading"
    binary-state-sort
    :rows-per-page-options="perPageOptions"
    @request="load"
  >
    <template v-slot:no-data>
      <div class="flex full-width column items-center q-py-lg">
        <q-icon name="mdi-cloud-search" class="q-mb-sm" size="40px" color="primary"></q-icon>
        <span class="block" style="font-size: 14px">No items were found.</span>
      </div>
    </template>
    <template v-slot:body-cell-status="props">
      <q-td style="width: 30px" :props="props">
        <StatusCell v-if="!props.row.deleted_at" :status="props.row.status" />
        <q-icon v-else name="close" color="grey" size="15px"></q-icon>
      </q-td>
    </template>
    <template v-slot:body-cell-objectId="props">
      <q-td :props="props">
        <div class="long-desc cursor-pointer" @click="copyValue(props.row.objectId, 'Object ID was copied')">
          {{ props.row.objectId }}
          <q-tooltip style="max-width: 480px"
            ><span>{{ props.row.objectId }}<br /></span>Click to copy</q-tooltip
          >
        </div>
      </q-td>
    </template>
    <template v-slot:body-cell-externalReference="props">
      <q-td :props="props">
        <div
          class="long-desc cursor-pointer"
          @click="copyValue(props.row.externalReference, 'External reference was copied')"
        >
          {{ props.row.externalReference }}
          <q-tooltip style="max-width: 480px; width: 480px"
            ><span>{{ props.row.externalReference }}<br /></span>Click to copy</q-tooltip
          >
        </div>
      </q-td>
    </template>
    <template v-slot:body-cell-digitalService="props">
      <q-td :props="props">
        <router-link :to="`/my-portfolio/${props.row.registration?.digitalService?.apmId}`">{{
          props.row.registration?.digitalService?.apmId
        }}</router-link>
      </q-td>
    </template>
    <template v-slot:body-cell-actions="props">
      <q-td :props="props">
        <q-btn
          flat
          round
          size="8px"
          style="width: 30px; height: 40px"
          class="btn--no-hover"
          :ripple="false"
          icon="engp:engp-ico-registry_outline"
          @click.stop="goToRegistration($event, props.row)"
        >
          <q-tooltip>Go to registration</q-tooltip>
        </q-btn>
        <WorkflowsBtn
          @showWorkflow="showWorkflow($event, props.row)"
          :loading="loading"
          :relatedWorkflowStatuses="relatedWorkflowStatuses.find((i) => i.id === props.row.id)?.statuses || []"
        />
        <q-btn
          v-if="mode === 'edit' && !props.row.deleted_at"
          flat
          round
          size="12px"
          @click="$emit('confirmDeleteAdfRecord', props.row)"
          :loading="loading"
          :disabled="loading || disableDeleteIds.find((item) => item.id === props.row.id)"
          icon="sym_r_delete"
          style="width: 30px; height: 40px"
          class="btn--no-hover"
        >
          <q-tooltip max-width="480px" style="white-space: pre-line" v-if="!loading">{{
            disableDeleteIds.find((item) => item.id === props.row.id)
              ? disableDeleteIds.find((item) => item.id === props.row.id).message
              : 'Delete'
          }}</q-tooltip>
        </q-btn>
        <CopyBtn :copyData="props.row.id" notification="Registration environment UUID was copied" />
      </q-td>
    </template>
  </q-table>
</template>

<script>
import dayjs from 'dayjs';
import { copyToClipboard } from 'quasar';
import { supportCntrlClick } from '@/shared/helpers';
import WorkflowsBtn from '@/shared/components/WorkflowsBtn.vue';
import { getRelatedWorkflowsStatuses, validateWorkFlow } from '@/pages/registrations/api/api';
import CopyBtn from '@/shared/components/CopyBtn.vue';
import StatusCell from '@/pages/registrations/components/registration-network/components/StatusCell.vue';
import { getAdfRecords } from '@/pages/allItems/api/api';

export default {
  name: 'AdfRecordsTable',
  components: {
    CopyBtn,
    StatusCell,
    WorkflowsBtn,
  },
  props: {
    rowsPerPageOptions: {
      type: Array,
      default: () => [5, 7, 10, 15, 20],
    },
    filters: {
      type: Object,
    },
    registrationId: {
      type: String,
    },
    registrationData: {
      type: Object,
    },
    mode: {
      type: String,
    },
  },
  data() {
    return {
      perPageOptions: this.rowsPerPageOptions,
      relatedWorkflowStatuses: [],
      disableDeleteIds: [],
      rows: [],
      loading: false,
      pagination: {
        sortBy: 'created_at',
        descending: true,
        page: 1,
        rowsPerPage: this.mode === 'list' ? 20 : 5,
      },
      cols: [
        {
          label: 'Status',
          name: 'status',
          field: 'status',
          align: 'left',
        },
        {
          label: 'Object ID',
          name: 'objectId',
          field: 'objectId',
          align: 'left',
        },
        {
          label: 'Environment',
          name: 'environment',
          field: 'environment',
          align: 'left',
        },
        {
          label: 'Provider',
          name: 'provider',
          field: 'provider',
          align: 'left',
        },
        {
          label: 'External Reference',
          name: 'externalReference',
          field: 'externalReference',
          align: 'left',
        },
        {
          label: 'Digital Service',
          name: 'digitalService',
          field: 'digitalService',
          align: 'left',
        },
        {
          label: 'Created at',
          name: 'created_at',
          field: 'created_at',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: 'Updated at',
          name: 'updated_at',
          field: 'updated_at',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: '',
          name: 'actions',
          field: 'actions',
          align: 'right',
        },
      ],
    };
  },
  methods: {
    copyValue(value, message) {
      copyToClipboard(value).then(() => {
        this.$notify('info', 'Info', message);
      });
    },
    goToRegistration(event, record) {
      supportCntrlClick(
        event,
        `/my-portfolio/${record.registration?.digitalService?.apmId}/registration/${record.registration.id}`,
      );
    },
    showWorkflow(event, record) {
      supportCntrlClick(event, `/workflows?itemId=${record.id}`);
    },
    async load(props) {
      this.loading = true;
      try {
        this.relatedWorkflowStatuses = [];
        this.disableDeleteIds = [];
        const limit = props.pagination.rowsPerPage;
        const { sortBy, descending } = props.pagination;
        const skip = props.pagination.page === 1 ? 0 : (props.pagination.page - 1) * props.pagination.rowsPerPage;
        const { id, registrationId, digitalService, status, environment, provider, objectId, dateFrom, dateTo } =
          this.filters;
        const { count, data } = await getAdfRecords(
          limit,
          skip,
          sortBy,
          descending,
          id,
          registrationId,
          digitalService,
          environment,
          provider,
          status,
          objectId,
          dateFrom,
          dateTo,
        );
        this.pagination.rowsNumber = count;
        this.rows = data;
        this.pagination.page = props.pagination.page;
        this.pagination.sortBy = sortBy;
        this.pagination.descending = descending;
        this.pagination.rowsPerPage = props.pagination.rowsPerPage;
        if (this.mode === 'list') {
          this.$router.replace({ query: this.buildQueryFromFilters() });
        }
        if (this.mode === 'edit' && this.filters.status !== 'removed') {
          const deletePrevalPromises = this.rows.map(async (adf) => {
            const data = {
              workflowId: 'engprt.delete-msbi-adf',
              inputVariables: {
                digitalServiceApmId: this.registrationData.digitalServiceApm,
                id: adf.id,
                environment: adf.environment,
              },
            };
            return validateWorkFlow(data).catch((res) => {
              this.disableDeleteIds.push({ id: adf.id, message: res.message });
            });
          });

          await Promise.all([...deletePrevalPromises]);
        }
        const relatedWorkflowsPrmises = this.rows.map((record) => {
          return getRelatedWorkflowsStatuses(record.id).then((response) => {
            this.relatedWorkflowStatuses.push({ id: record.id, statuses: response });
          });
        });
        await Promise.all([...relatedWorkflowsPrmises]);
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    buildQueryFromFilters() {
      const query = {};
      for (const key in this.filters) {
        if (this.filters[key]) {
          query[key] = this.filters[key];
        }
      }
      query[this.pagination.sortBy] = this.pagination.descending ? 'DESC' : 'ASC';
      return query;
    },
    reload() {
      this.$refs.adfRecordsTable.requestServerInteraction();
    },
  },
  mounted() {
    if (this.mode === 'list') {
      const query = this.$route.query;
      const sortFields = ['created_at', 'updated_at'];

      for (const field of sortFields) {
        if (query?.[field]) {
          this.pagination.sortBy = field;
          this.pagination.descending = query[field] !== 'ASC';
        }
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.long-desc {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
